import {TimeFrame, TimeFrameRange, TimeFrameRanges} from "../models/TimeFrame";
import {Pair, PairOf, Transform} from "../../types/GenericTypes";
import {DateTimeUtil} from "../../js/util/DateTimeUtil";
import moment from "moment";
import {IsoDate} from "../../pseudo-types/DateTypes";

export class TimeFrameUtil {

    public static timeFrameRanges(dates: ReadonlyArray<IsoDate>): TimeFrameRanges {
        const ytd = this.range(TimeFrame.YTD);
        const lfy = this.range(TimeFrame.LFY);
        const fytd = this.range(TimeFrame.FYTD);

        const startFinderFn = this.dateOnOrAfter(dates);
        const endFinderFn = this.lastDateOnOrBefore(dates);

        // find the closes date we have to the calculated start, this is because the start may be a non-trading day
        // and thus not have any data for it
        const ytdStart = startFinderFn(ytd[0]);
        const ytdEnd = endFinderFn(ytd[1]);

        const lfyStart = startFinderFn(lfy[0]);
        const lfyEnd = endFinderFn(lfy[1]);

        const fytdStart = startFinderFn(fytd[0]);
        const fytdEnd = endFinderFn(fytd[1]);

        const result: Array<TimeFrameRange>  = [];

        if ((fytdStart != null) && (fytdEnd != null)) {
            const finYearToDate = TimeFrameRange.of(TimeFrame.FYTD, fytdStart, fytdEnd);
            result.push(finYearToDate);
        }

        if ((lfyStart != null) && (lfyEnd != null)) {
            const lastFinYear = TimeFrameRange.of(TimeFrame.LFY, lfyStart, lfyEnd);
            result.push(lastFinYear);
        }

        if ((ytdStart != null) && (ytdEnd != null)) {
            const yearToDate = TimeFrameRange.of(TimeFrame.YTD, ytdStart, ytdEnd);
            result.push(yearToDate);
        }

        return result;
    }

    private static range(timeFrame: TimeFrame): Pair<IsoDate> {
        const now = moment();
        const isInNewFinancialYear = now.month() >= 6;

        const startOfYear = now.clone()
                               .startOf("year");
        const startOfPreviousYearFinancialYear = now.clone()
                                                    .subtract(1, "year")
                                                    .month("Jul")
                                                    .startOf("month");
        const endOfPreviousYearFinancialYear = now.clone()
                                                  .month("Jun")
                                                  .endOf("month")
                                                  .endOf("milliseconds");

        const startOfCurrentYearFinancialYear = now.clone()
                                                   .month("Jul")
                                                   .startOf("month");
        const endOfCurrentYearFinYear = now.clone()
                                           .add(1, "year")
                                           .month("Jun")
                                           .endOf("month")
                                           .endOf("milliseconds");

        const startOfCurrentFinancialYear = isInNewFinancialYear ? startOfCurrentYearFinancialYear : startOfPreviousYearFinancialYear;
        const endOfCurrentFinancialYear = isInNewFinancialYear ? endOfCurrentYearFinYear : endOfPreviousYearFinancialYear;
        const startOfLastFinancialYear = startOfCurrentFinancialYear.clone()
                                                                    .subtract(1, "year");
        const endOfLastFinancialYear = endOfCurrentFinancialYear.clone()
                                                                .subtract(1, "year");

        switch (timeFrame) {
            case TimeFrame.YTD: {
                const start = DateTimeUtil.momentToIsoDate(startOfYear);
                const end = DateTimeUtil.momentToIsoDate(now);
                return PairOf(start, end);
            }
            case TimeFrame.LFY: {
                const start = DateTimeUtil.momentToIsoDate(startOfLastFinancialYear);
                const end = DateTimeUtil.momentToIsoDate(endOfLastFinancialYear);
                return PairOf(start, end);
            }
            case TimeFrame.FYTD: {
                const start = DateTimeUtil.momentToIsoDate(startOfCurrentFinancialYear);
                const end = DateTimeUtil.momentToIsoDate(now);
                return PairOf(start, end);
            }
            case TimeFrame.CUSTOM:
                throw Error("CUSTOM range not supported here");
        }
    }

    private static dateOnOrAfter(dates: ReadonlyArray<IsoDate>): Transform<IsoDate, IsoDate | undefined> {
        const sortedDates = [...dates].sort(DateTimeUtil.sort);
        return (target) => sortedDates.find((d) => (d === target) || (d > target));
    }

    private static lastDateOnOrBefore(dates: ReadonlyArray<IsoDate>): Transform<IsoDate, IsoDate | undefined> {
        const sortedDates = [...dates].sort(DateTimeUtil.sort).reverse();
        return (target) => sortedDates.find((d) => (d === target) || (d < target));
    }
}
